// ChartProvider.tsx
import React from 'react';

type QueryLikeResult<TData> = {
  data?: TData;
  isLoading?: boolean;
  error?: unknown;
};

type ProviderHook<TData> = () => QueryLikeResult<TData>;

interface ChartProviderProps<TData> {
  children: React.ReactNode;
  provider: ProviderHook<TData>;
  /**
   * Maps provider data -> props injected into each child component
   * (e.g. { data: ..., max: ... }).
   * If omitted, ChartProvider injects { data }.
   */
  mapDataToChildProps?: (data: TData | undefined) => Record<string, any>;
  /**
   * Optional: also inject loading/error if you want children to react to them.
   */
  injectMeta?: boolean;
}

export function ChartProvider<TData>({
  provider: useData,
  children,
  mapDataToChildProps,
  injectMeta = false,
}: ChartProviderProps<TData>) {
  const { data, isLoading, error } = useData();

  const injectedProps = mapDataToChildProps
    ? mapDataToChildProps(data)
    : { data };

  const metaProps = injectMeta ? { isLoading, error } : {};

  return (
    <>
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) return child;
        return React.cloneElement(child as React.ReactElement<any>, {
          ...injectedProps,
          ...metaProps,
        });
      })}
    </>
  );
}